import apiClient from './api'

const mailService = {
  async getInbox(lastMailId = null, params = {}) {
    try {
      let url = '/admin/mails?type=inbox&_with_[senderDepartment]=&_with_[userFolders]=&_with_[marginalizations.marginalization]=&_sort_=id:desc&_limit_=10';
      
      // Add cursor-based pagination if lastMailId is provided
      if (lastMailId) {
        url += `&_filters_[]=id__lt&id__lt=${lastMailId}`;
      }
      
      //console.log("Calling inbox API with URL:", url, "lastMailId:", lastMailId);
      const response = await apiClient.get(url, { params });
      //console.log("Inbox API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Inbox API error:", error);
      throw error.response?.data || error;
    }
  },

  async getMail(mailId) {
    try {
      const response = await apiClient.get(`/admin/mails/${mailId}?_with_[to_departments]=&_with_[cc_departments]=&_with_[sender.department]=&_with_[marginalizations.marginalization]=`)
      return response.data
    } catch (error) {
      throw error.response?.data || error
    }
  },

  async getSentMails(lastMailId = null, params = {}) {
    try {
      // Since we're filtering by id__lt, we should sort by id:desc to maintain consistency
      let url = '/admin/mails?type=sent&_with_[marginalizations.marginalization]=&_with_[senderDepartment]=&_sort_=sent_at:desc&_limit_=10';
      
      // Add cursor-based pagination if lastMailId is provided
      if (lastMailId) {
        url += `&_filters_[]=id__lt&id__lt=${lastMailId}`;
      }
      
      //console.log("Calling sent mails API with URL:", url, "lastMailId:", lastMailId);
      const response = await apiClient.get(url, { params });
      //console.log("Sent mails API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Sent mails API error:", error);
      throw error.response?.data || error;
    }
  },

  async getArchiveMails(params = {}) {
    try {
      const url = '/admin/archive-mails?_with_[senderDepartment]=&_with_[sender.employeeTitle]=&_with_[marginalizations.marginalization]=&_sort_=id:desc&_no_pagination_=1';
      
      //console.log("Calling archive mails API with URL:", url);
      const response = await apiClient.get(url, { params });
      //console.log("Archive mails API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Archive mails API error:", error);
      throw error.response?.data || error;
    }
  },

  async getPriorityMails( limit = 5) {
    try {
      const params = { 
        
        limit: limit
      }
      const response = await apiClient.get('/admin/mails?type=inbox&_with_[senderDepartment]=&_with_[departments]=&_with_[userFolders]=&_with_[marginalizations.marginalization]=&_sort_=id:desc&_no_pagination_=1&_filters_[]=priority__level__eq&priority__level__eq=1', { params })
      return response.data
    } catch (error) {
      throw error.response?.data || error
    }
  },

  async getTodayReceivedCount() {
    try {
      // Get inbox mails and count the ones received today
      const response = await this.getInbox()
      const today = new Date().toISOString().split('T')[0]
      
      const mails = response.data?.data || response.data || []
      const todayReceivedCount = mails.filter(mail => {
        const mailDate = new Date(mail.sent_at || mail.created_at).toISOString().split('T')[0]
        return mailDate === today
      }).length
      
      return { count: todayReceivedCount }
    } catch (error) {
      throw error.response?.data || error
    }
  },

  async getTodaySentCount(userId) {
    try {
      // Get sent mails and count the ones sent today
      const response = await this.getSentMails(userId)
      const today = new Date().toISOString().split('T')[0]
      
      const mails = response.data?.data || response.data || []
      const todaySentCount = mails.filter(mail => {
        const mailDate = new Date(mail.sent_at || mail.created_at).toISOString().split('T')[0]
        return mailDate === today
      }).length
      
      return { count: todaySentCount }
    } catch (error) {
      throw error.response?.data || error
    }
  },

  async moveMail(mailId, folderId) {
    try {
      const response = await apiClient.post(`/admin/mails/${mailId}/move`, {
        folder_id: folderId
      })
      return response.data
    } catch (error) {
      throw error.response?.data || error
    }
  },

  async moveMailToInbox(mailId) {
    try {
      const response = await apiClient.post(`/admin/mails/${mailId}/move`, {
        folder_id: null
      })
      return response.data
    } catch (error) {
      throw error.response?.data || error
    }
  },

  async addMarginalizationToMail(mailId, marginalizationId, departments = []) {
    try {
      const requestData = {
        mail_id: mailId,
        marginalization_id: marginalizationId
      };
      
      // Add departments array if provided
      if (departments && departments.length > 0) {
        requestData.departments = departments;
      }
      
      const response = await apiClient.post('/admin/mail-marginalizations', requestData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async removeMarginalizationFromMail(mailMarginalizationId) {
    try {
      const response = await apiClient.delete(`/admin/mail-marginalizations/${mailMarginalizationId}`)
      return response.data
    } catch (error) {
      throw error.response?.data || error
    }
  },

  async deleteMail(mailId) {
    try {
      const response = await apiClient.delete(`/admin/mails/${mailId}`)
      return response.data
    } catch (error) {
      throw error.response?.data || error
    }
  },

  async sendMail(formData) {
    try {
      const response = await apiClient.post('/admin/mails', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      throw error.response?.data || error
    }
  },

  // Get mail history
  async getMailHistory(mailId) {
    try {
      const response = await apiClient.get(`/admin/mails/${mailId}/history`)
      return response.data
    } catch (error) {
      throw error.response?.data || error
    }
  },

  // Mark mail as printed
  async markAsPrinted(mailId) {
    try {
      const response = await apiClient.post(`/admin/mails/${mailId}/mark-as-printed`)
      return response.data
    } catch (error) {
      throw error.response?.data || error
    }
  }
}

export default mailService 